import React from 'react';
import { currency } from 'utils/currency';
import { TenantsParkingData } from 'types';
import { getDate, getStatusColor, parseInvoicesStatus, capitalizeStatus } from './utils';
import { InvoiceStatus, TenantNameSort } from 'utils/constants';
import AGTableWrapper from 'components/AGTable/AGTableWrapper';
import TriangleCaretIcon from 'components/Icons/TriangleCaretIcon';
import styles from './TenantsParking.module.css';

type TenantsParkingTableProps = {
    data: TenantsParkingData[] | null;
    nameSort?: TenantNameSort;
    onNameSortClick?: () => void;
    onRowClick?: (row: TenantsParkingData) => void;
    isDemoMode?: boolean;
};

type TenantsParkingRowProps = {
    rowData: TenantsParkingData | null;
    onRowClick?: (row: TenantsParkingData) => void;
    isDemoMode?: boolean;
};

const TableRow = ({ rowData, onRowClick, isDemoMode = false }: TenantsParkingRowProps) => {
    if (!rowData) return <></>;
    const totalAmount = (rowData.price_per_slot / 100) * rowData.slot_count;
    const totalAmountContent = <td>{currency.format(totalAmount)}</td>;
    const contact = rowData.owner.email ? rowData.owner.email : '';
    const startDate = getDate(rowData.start_date);
    const endDate = rowData.end_date ? getDate(rowData.end_date) : 'until they cancel';
    const status: InvoiceStatus = rowData.invoice_status
        ? parseInvoicesStatus(rowData.invoice_status)
        : InvoiceStatus.NA;

    const statusColor = getStatusColor(status);
    const capitalizedStatus = status === InvoiceStatus.NA ? InvoiceStatus.NA : capitalizeStatus(status);

    return (
        <tr onClick={() => onRowClick?.(rowData)} className={onRowClick ? styles.clickableRow : undefined}>
            <td className={styles.nameCell} title={rowData.description || ''}>
                {rowData.description}
            </td>
            <td>
                {rowData.active_rentals}/{rowData.slot_count}
            </td>
            {totalAmountContent}
            <td className={styles.noWrapCell}>
                <span className={styles.dot} style={{ backgroundColor: statusColor }}></span>
                {capitalizedStatus}
            </td>
            <td className={isDemoMode ? 'blur' : ''}>{contact}</td>
            <td className={styles.noWrapCell}>
                {startDate} - {endDate}
            </td>
        </tr>
    );
};

const Table = ({ data, nameSort, onNameSortClick, onRowClick, isDemoMode = false }: TenantsParkingTableProps) => {
    const upCaretColor = nameSort === 'asc' ? 'var(--ag-sand-12)' : 'var(--ag-sand-8)';
    const downCaretColor = nameSort === 'desc' ? 'var(--ag-sand-12)' : 'var(--ag-sand-8)';

    return (
        <AGTableWrapper>
            <thead>
                <tr>
                    <th className={styles.sortableTh}>
                        <button type="button" className={styles.sortableHeader} onClick={onNameSortClick}>
                            Name
                            <span className={styles.sortCaretStack}>
                                <TriangleCaretIcon direction="up" color={upCaretColor} />
                                <TriangleCaretIcon direction="down" color={downCaretColor} />
                            </span>
                        </button>
                    </th>
                    <th>Spaces</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Contact</th>
                    <th>Period</th>
                </tr>
            </thead>
            <tbody>
                {data &&
                    data.length > 0 &&
                    data.map((rowData) => (
                        <TableRow
                            key={rowData.uuid}
                            rowData={rowData}
                            onRowClick={onRowClick}
                            isDemoMode={isDemoMode}
                        />
                    ))}
            </tbody>
        </AGTableWrapper>
    );
};

export default Table;
